<template>
  <div :class="{ 'hide': !audio }" class="audioguide">
    <v-fade-transition mode="out-in">
      <button
        v-show="showStop"
        type="button"
        @click="stopAudio()"
      >
        <ReloadSvg />
      </button>
    </v-fade-transition>
    <v-fade-transition mode="out-in">
      <button
        v-if="!isPlaying"
        type="button"
        @click="playAudio()"
      >
        <HeadsetsSvg />
      </button>
      <button
        v-else-if="isPlaying"
        type="button"
        @click="pauseAudio()"
      >
        <PauseSvg />
      </button>
    </v-fade-transition>
    <v-fade-transition mode="out-in">
      <button
        type="button"
        @click="$events.$emit('toggle-audioguide-modal', true, room)"
      >
        <OpenAudioguideIcon />
      </button>
    </v-fade-transition>
    <h4 class="d-none d-sm-block">{{ $root.allLanguages[$language.current].listenAudioguide }}&nbsp;-&nbsp;</h4>
    <h4 v-show="!!duration" class="duration">{{ duration }}</h4>
    <audio
      ref="audio"
      :src="'/media/' + audio"
      @canplay="onAudioReady()"
      @play="onAudioPlay()"
      @pause="onAudioPause()"
      @timeupdate="onTimeUpdate()"
    />
  </div>
</template>

<script>
import { formatTime } from 'plyr/src/js/utils/time'
import OpenAudioguideIcon from '@/assets/svg/open-audioguide.svg'
import HeadsetsSvg from '@/assets/svg/headsets.svg'
import ReloadSvg from '@/assets/svg/reload.svg'
import PauseSvg from '@/assets/svg/pause.svg'
import { gsap } from 'gsap'

export default {
  name: 'Audioguide',
  components: { HeadsetsSvg, OpenAudioguideIcon, ReloadSvg, PauseSvg },
  async mounted () {
    this.$events.on('room-has-audioguide', this.onRoomChange)
    this.$events.on('room-has-no-audioguide', this.onRoomChange)
    this.$events.on('room-audio-play', () => { if (this.audio) this.pauseAudio() })
    // this.$events.on('room-audio-pause', () => { if (this.audio) this.playAudio() })
    this.$events.on('change-language', this.onLanguageChange)
    await this.$nextTick()
    this.createTimeline()
  },
  data () {
    return {
      audio: null,
      duration: null,
      showStop: false,
      isPlaying: false,
      roomAudio: {},
      room: null
    }
  },
  computed: {
    player () {
      return this.$refs.audio
    }
  },
  methods: {
    createTimeline () {
      this.tl = gsap.timeline({ paused: true })
      this.tl.addLabel('pause')
      this.tl.fromTo(this.player, { volume: 0 }, { volume: 1, duration: 0.5 })
      this.tl.addLabel('play')
    },
    playAudio () {
      this.player.play()
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'generic_event',
          eventCategory: 'Audioguide Garden',
          eventAction: 'Play Audioguide',
          eventLabel: this.room.enTitle
        })
      }
    },
    pauseAudio () {
      this.tl.tweenTo('pause', { onComplete: () => { this.player.pause() } })
    },
    stopAudio () {
      this.player.pause()
      this.player.currentTime = 0
      this.showStop = false
      this.isPlaying = false
    },
    onAudioReady () {
      this.onTimeUpdate()
    },
    onAudioPlay () {
      this.$events.$emit('audioguide-play')
      this.isPlaying = true
      this.showStop = true
      this.tl.tweenTo('play')
    },
    onAudioPause () {
      this.$events.$emit('audioguide-pause')
      this.isPlaying = false
    },
    onTimeUpdate () {
      this.duration = formatTime(this.player.duration - this.player.currentTime)
    },
    onRoomChange (room) {
      this.room = room
      if (!this.room && this.isPlaying) {
        this.tl.tweenTo('pause', {
          onComplete: () => {
            this.stopAudio()
            this.audio = null
          }
        })
        return
      } else if (!this.room) {
        this.roomAudio = null
        this.audio = null
        return
      }
      this.roomAudio = room
      if (this.audio === this.roomAudio[this.$language.current + 'Audioguide'].id) return
      if (!this.isPlaying) {
        this.audio = this.roomAudio[this.$language.current + 'Audioguide'].id
      } else {
        this.tl.tweenTo('pause', {
          onComplete: () => {
            this.stopAudio()
            this.audio = this.roomAudio[this.$language.current + 'Audioguide'].id
          }
        })
      }
    },
    onLanguageChange (language) {
      if (this.roomAudio && this.roomAudio[language + 'Audioguide']) {
        this.stopAudio()
        this.audio = this.roomAudio[language + 'Audioguide'].id
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.audioguide {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  &.hide {
    display: none;
  }

  button {
    pointer-events: all;
    outline: none;
    position: relative;
    width: 32px;
    height: 32px;
    background-color: $c-light-pink;
    border-radius: 50%;
    margin-right: 8px;

    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  h4 {
    font-style: normal;
    font-weight: normal;
    font-size: 20px;
    line-height: 100%;
    text-align: center;
    letter-spacing: 0.01em;

    &.duration {
      min-width: 50px;
      opacity: 0.5;
    }
  }
}
</style>
